import axios from 'axios';
import { Capacitor } from '@capacitor/core';
import { Preferences } from '@capacitor/preferences';

const BACKEND_URL = process.env.REACT_APP_BACKEND_URL;
export const API = `${BACKEND_URL}/api`;

const TOKEN_KEY = 'nia_token';
const CSRF_TOKEN_KEY = 'nia_csrf_token';
const DEVICE_ID_KEY = 'nia_device_id';
const REMEMBERED_IDENTITY_KEY = 'nia_remembered_identity';

/**
 * Token storage strategy
 * ----------------------
 * We use **the strongest storage available on each platform**:
 *
 *   - On native iOS  → Capacitor Preferences (backed by Keychain).
 *   - On native Android → Capacitor Preferences (backed by EncryptedSharedPreferences).
 *   - On the web → `sessionStorage` (cleared on tab close, narrower XSS surface
 *     than `localStorage`). httpOnly cookies are not viable while the preview
 *     is on a different origin than the API.
 *
 * Capacitor Preferences is async, but the axios interceptor needs a sync read.
 * We solve that by maintaining an **in-memory mirror** that is hydrated once at
 * app boot via {@link bootstrapTokenStore} and kept in sync on every `setToken`.
 * Reads after boot are O(1) sync; writes are sync + fire-and-forget async.
 *
 * Legacy `localStorage` tokens (from web sessions before the migration) are
 * pulled into sessionStorage / Preferences once and purged.
 */
const isNative = Capacitor.isNativePlatform();
let _memToken = null;
let _bootHydrationPromise = null;

function _readWebToken() {
    try {
        return window.sessionStorage?.getItem(TOKEN_KEY) || null;
    } catch (e) {
        return null;
    }
}

function _writeWebToken(t) {
    try {
        if (t) window.sessionStorage.setItem(TOKEN_KEY, t);
        else window.sessionStorage.removeItem(TOKEN_KEY);
    } catch (e) {
        console.debug('sessionStorage unavailable (private-mode / storage disabled); auth token in-memory only', e);
    }
}

function _migrateLegacyLocalStorage() {
    try {
        if (typeof window === 'undefined') return null;
        const legacy = window.localStorage?.getItem(TOKEN_KEY);
        if (!legacy) return null;
        window.localStorage.removeItem(TOKEN_KEY);
        return legacy;
    } catch (e) {
        return null;
    }
}

/**
 * Hydrate the in-memory token mirror from the strongest store available.
 * Call once at app boot before any authenticated request fires.
 */
export async function bootstrapTokenStore() {
    if (_bootHydrationPromise) return _bootHydrationPromise;
    _bootHydrationPromise = (async () => {
        const legacy = _migrateLegacyLocalStorage();
        if (isNative) {
            try {
                const { value } = await Preferences.get({ key: TOKEN_KEY });
                _memToken = value || legacy || null;
                if (legacy && !value) {
                    await Preferences.set({ key: TOKEN_KEY, value: legacy });
                }
                // V3.11 — also restore the CSRF token mirror so the first
                // POST after a cold start doesn't fail CSRF enforcement.
                try {
                    const csrf = await Preferences.get({ key: CSRF_TOKEN_KEY });
                    if (csrf?.value) _memCsrf = csrf.value;
                } catch (e) {
                    console.debug('csrf-token restore from Preferences failed', e);
                }
            } catch (e) {
                _memToken = legacy || null;
            }
        } else {
            _memToken = _readWebToken() || legacy || null;
            if (legacy && !_readWebToken()) _writeWebToken(legacy);
        }
        return _memToken;
    })();
    return _bootHydrationPromise;
}

export function setToken(t) {
    _memToken = t || null;
    if (isNative) {
        // fire-and-forget — keychain write is fast and shouldn't block the UI
        if (t) Preferences.set({ key: TOKEN_KEY, value: t }).catch(() => null);
        else Preferences.remove({ key: TOKEN_KEY }).catch(() => null);
    } else {
        _writeWebToken(t);
    }
}

export function getToken() {
    if (_memToken !== null) return _memToken;
    // Web fallback: try a sync read in case bootstrap hasn't completed yet.
    if (!isNative) {
        const t = _readWebToken();
        if (t) _memToken = t;
        return _memToken;
    }
    return null;
}

// ---------------------------------------------------------------------------
// Phase 17 — device-bound PIN unlock
// ---------------------------------------------------------------------------
// `device_id` is a stable UUID v4 we generate ONCE per install and persist in
// the same secure storage as the JWT. It is purely an unlock binding — it
// does not authenticate, does not authorize, and never appears in any other
// API call. Losing it simply forces a one-time OTP re-login.
//
// `remembered_identity` is the minimal user record we cache so the PIN
// unlock screen can greet the user by name without a network call.

function _uuid4() {
    if (typeof crypto !== 'undefined' && crypto.randomUUID) return crypto.randomUUID();
    // Fallback for very old browsers / private mode.
    return 'xxxxxxxx-xxxx-4xxx-yxxx-xxxxxxxxxxxx'.replace(/[xy]/g, (c) => {
        const r = (Math.random() * 16) | 0;
        const v = c === 'x' ? r : (r & 0x3) | 0x8;
        return v.toString(16);
    });
}

function _readLocal(key) {
    try { return window.localStorage?.getItem(key) || null; } catch { return null; }
}
function _writeLocal(key, value) {
    try {
        if (value) window.localStorage.setItem(key, value);
        else window.localStorage.removeItem(key);
    } catch (e) {
        console.debug('localStorage unavailable; PIN device_id in-memory only', e);
    }
}

let _memDeviceId = null;

/**
 * Return the persistent device identifier, creating one on first call.
 * Persisted in:
 *   - Capacitor Preferences (Keychain / EncryptedSharedPreferences) on native
 *   - localStorage on web (it MUST persist across sessions, unlike sessionStorage
 *     which is wiped on tab close — losing it would force an OTP every time).
 */
export async function getDeviceId() {
    if (_memDeviceId) return _memDeviceId;
    if (isNative) {
        try {
            const { value } = await Preferences.get({ key: DEVICE_ID_KEY });
            if (value) { _memDeviceId = value; return value; }
        } catch (e) {
            console.debug('Preferences read failed, falling back', e);
        }
    } else {
        const existing = _readLocal(DEVICE_ID_KEY);
        if (existing) { _memDeviceId = existing; return existing; }
    }
    const fresh = _uuid4();
    _memDeviceId = fresh;
    if (isNative) {
        Preferences.set({ key: DEVICE_ID_KEY, value: fresh }).catch(() => null);
    } else {
        _writeLocal(DEVICE_ID_KEY, fresh);
    }
    return fresh;
}

/** Synchronous best-effort device id (returns null if not yet hydrated). */
export function getDeviceIdSync() {
    if (_memDeviceId) return _memDeviceId;
    if (!isNative) {
        const v = _readLocal(DEVICE_ID_KEY);
        if (v) _memDeviceId = v;
        return _memDeviceId;
    }
    return null;
}

/** Snapshot of identifying user fields that the PIN screen can use. */
export function setRememberedIdentity(user) {
    const minimal = user
        ? { id: user.id, email: user.email || null, name: user.name || null, role: user.role || null }
        : null;
    if (isNative) {
        if (minimal) Preferences.set({ key: REMEMBERED_IDENTITY_KEY, value: JSON.stringify(minimal) }).catch(() => null);
        else Preferences.remove({ key: REMEMBERED_IDENTITY_KEY }).catch(() => null);
    } else {
        _writeLocal(REMEMBERED_IDENTITY_KEY, minimal ? JSON.stringify(minimal) : null);
    }
}

export async function getRememberedIdentity() {
    if (isNative) {
        try {
            const { value } = await Preferences.get({ key: REMEMBERED_IDENTITY_KEY });
            return value ? JSON.parse(value) : null;
        } catch { return null; }
    }
    const raw = _readLocal(REMEMBERED_IDENTITY_KEY);
    try { return raw ? JSON.parse(raw) : null; } catch { return null; }
}


const client = axios.create({
    baseURL: API,
    timeout: 15000,
    // V3.11 — credentialed requests so the httpOnly nia_session cookie is
    // sent with every API call. Backend explicitly allowlists the preview
    // + production origins (see backend/.env CORS_ORIGINS).
    withCredentials: true,
});

/**
 * V3.11 — CSRF token mirror.
 *
 * The double-submit cookie pattern requires our frontend JS to read the
 * non-httpOnly ``nia_csrf`` cookie and echo it back as the ``X-CSRF-Token``
 * header on every mutation. In Capacitor WebView and some preview
 * deployments, JS reading `document.cookie` returns empty even when the
 * cookie was set — so we ALSO mirror the token from the login response
 * body into sessionStorage (or Preferences on native) as a fallback.
 *
 * Order of trust on every read:
 *   1. document.cookie (canonical, always-fresh after the next response)
 *   2. in-memory mirror (set by login / OTP verify / PIN unlock handlers)
 *   3. sessionStorage / Preferences fallback (survives reload but NOT
 *      cross-domain; matches the cookie's effective scope)
 */
let _memCsrf = null;

function _readCookieValue(name) {
    if (typeof document === 'undefined' || !document.cookie) return null;
    const target = `${name}=`;
    for (const raw of document.cookie.split(';')) {
        const c = raw.trim();
        if (c.startsWith(target)) {
            try { return decodeURIComponent(c.slice(target.length)) || null; }
            catch { return c.slice(target.length) || null; }
        }
    }
    return null;
}

export function getCsrfToken() {
    const cookieValue = _readCookieValue('nia_csrf');
    if (cookieValue) {
        _memCsrf = cookieValue;
        return cookieValue;
    }
    if (_memCsrf) return _memCsrf;
    try { return window.sessionStorage?.getItem(CSRF_TOKEN_KEY) || null; }
    catch { return null; }
}

export function setCsrfToken(token) {
    _memCsrf = token || null;
    try {
        if (token) window.sessionStorage?.setItem(CSRF_TOKEN_KEY, token);
        else window.sessionStorage?.removeItem(CSRF_TOKEN_KEY);
    } catch (e) {
        console.debug('csrf-token sessionStorage write failed (private-mode?)', e);
    }
    if (isNative) {
        if (token) Preferences.set({ key: CSRF_TOKEN_KEY, value: token }).catch(() => null);
        else Preferences.remove({ key: CSRF_TOKEN_KEY }).catch(() => null);
    }
}

const _CSRF_REQUIRED_METHODS = new Set(['post', 'put', 'patch', 'delete']);

client.interceptors.request.use((cfg) => {
    const t = getToken();
    if (t) cfg.headers.Authorization = `Bearer ${t}`;

    // V3.11 — attach the CSRF header on every state-changing call, so we
    // always satisfy the backend's CsrfProtectMiddleware regardless of
    // which auth surface (cookie vs Bearer) ends up authenticating us.
    const method = (cfg.method || 'get').toLowerCase();
    if (_CSRF_REQUIRED_METHODS.has(method)) {
        const csrf = getCsrfToken();
        if (csrf) cfg.headers['X-CSRF-Token'] = csrf;
    }
    return cfg;
});

client.interceptors.response.use(
    (r) => r,
    (err) => {
        if (err.response && err.response.status === 401) {
            setToken(null);
            setCsrfToken(null);
        }
        return Promise.reject(err);
    }
);

export default client;
export { client };

export function receiptUrl(activityId) {
    // LEGACY: full-JWT-in-URL pattern. Kept for backward compatibility with
    // any old tabs. New callers must use `openReceipt(activityId)` which
    // hits POST /receipts/{id}/sign and opens a short-lived signed URL
    // instead of leaking the user's long-lived JWT in browser history.
    const tok = getToken();
    return `${API}/receipts/${activityId}.pdf?token=${encodeURIComponent(tok || '')}`;
}

/**
 * Open a receipt PDF in a new tab using a short-lived scoped token.
 *
 * Flow:
 *   1) POST /api/receipts/{id}/sign  ->  { download_url, expires_in_seconds }
 *   2) window.open(BACKEND_URL + download_url, '_blank')
 *
 * The download_url embeds a JWT that:
 *   - is scoped to this single activity_id (cannot download other receipts)
 *   - expires in ~5 minutes
 * so even if it leaks via Referer / history / access logs, the blast
 * radius is one PDF for a few minutes \u2014 not the user's whole session.
 */
export async function openReceipt(activityId) {
    try {
        const { data } = await client.post(`/receipts/${activityId}/sign`);
        const url = `${API.replace(/\/api$/, '')}${data.download_url}`;
        window.open(url, '_blank', 'noopener,noreferrer');
        return { ok: true };
    } catch (e) {
        return { ok: false, error: e?.response?.data?.detail || e?.message || 'Failed to open receipt' };
    }
}

export const api = {
    // ===== Auth =====
    sendEmailOtp: (email) => client.post('/auth/send-otp/email', { email }),
    sendPhoneOtp: (phone, country, role) => client.post('/auth/send-otp/phone', { phone, country, role }),
    verifyOtp: (payload) => client.post('/auth/verify-otp', payload),
    me: () => client.get('/auth/me'),
    updateProfile: (payload) => client.put('/auth/profile', payload),
    updateNotif: (payload) => client.put('/auth/notifications', payload),
    completeOnboarding: (payload) => client.post('/auth/onboarding', payload),
    logout: () => client.post('/auth/logout'),
    deleteAccount: (payload) => client.post('/auth/delete-account', payload || {}),
    exportData: () => client.get('/auth/export'),

    // ===== App Store reviewer demo mode =====
    demoStatus: () => client.get('/auth/demo/status'),
    demoSwitchPersona: (persona) => client.post('/auth/demo/switch-persona', { persona }),
    resetDemo: () => client.post('/demo/reseed'),
    runSchedulerTick: (ref) => client.post('/demo/run-scheduler-tick', null, { params: ref ? { ref } : {} }),

    // ===== Notifications =====
    listNotifications: (unreadOnly = false) => client.get('/notifications', { params: { unread_only: unreadOnly } }),
    markNotifRead: (id) => client.post(`/notifications/${id}/read`),
    markAllNotifRead: () => client.post('/notifications/read-all'),

    // ===== Sender — Family =====
    listFamily: () => client.get('/sender/family'),
    getFamilyMember: (id) => client.get(`/sender/family/${id}`),
    addFamily: (p) => client.post('/sender/family', p),
    updateFamily: (id, p) => client.put(`/sender/family/${id}`, p),
    deleteFamily: (id) => client.delete(`/sender/family/${id}`),

    // ===== Sender — Support Plans (Obligations) =====
    listObligations: (memberId) => client.get(`/sender/family/${memberId}/obligations`),
    createObligation: (memberId, payload) =>
        client.post(`/sender/family/${memberId}/obligations`, payload),
    updateObligation: (obligationId, payload) =>
        client.put(`/sender/obligations/${obligationId}`, payload),
    deleteObligation: (obligationId) =>
        client.delete(`/sender/obligations/${obligationId}`),
    upcomingObligations: (days = 30) =>
        client.get('/sender/obligations/upcoming', { params: { days } }),

    // ===== Sender — Home & Activity =====
    senderHome: () => client.get('/sender/home'),
    senderActivity: (filter = 'all') => client.get('/sender/activity', { params: { filter } }),

    // ===== Support tickets (Contact Support) =====
    submitTicket: (payload) => client.post('/tickets', payload),

    // ===== School calendar (shared) =====
    getSchoolCalendar: (country) => client.get(`/school-calendar/${country}`),

    // ===== Admin console — core =====
    adminMe: () => client.get('/admin/me'),
    adminOverview: (params) => client.get('/admin/overview', { params }),
    adminFx: () => client.get('/admin/fx'),
    adminSettings: () => client.get('/admin/settings'),
    adminUpdateSettings: (payload) => client.put('/admin/settings', payload),
    adminAuditLogs: (params) => client.get('/admin/audit-logs', { params }),

    // ===== Admin — Users =====
    adminListUsers: (params) => client.get('/admin/users', { params }),
    adminGetUser: (id) => client.get(`/admin/users/${id}`),
    adminUsersDiagnostics: (params) => client.get('/admin/users/diagnostics', { params }),
    adminNormalizeCreatedAt: (dryRun = true) => client.post('/admin/users/normalize-created-at', null, { params: { dry_run: dryRun } }),
    adminUpcomingSupport: (params) => client.get('/admin/upcoming-support', { params }),
    adminNudgeUser: (userId, payload) => client.post(`/admin/users/${userId}/nudge`, payload),
    adminListRecipients: (params) => client.get('/admin/recipients', { params }),

    // ===== Admin — Notifications + Board + Exports =====
    adminNotifications: (params) => client.get('/admin/notifications', { params }),
    adminBoardKpis: (params) => client.get('/admin/board-kpis', { params }),
    adminExport: (kind) => client.get(`/admin/exports/${kind}.csv`, { responseType: 'blob' }),

    // ===== Admin analytics (v1 subset) =====
    adminAnalyticsOverview: (params) => client.get('/admin/analytics/overview', { params }),
    adminAnalyticsLeaderboards: (params) => client.get('/admin/analytics/leaderboards', { params }),

    // ===== Admin — Receipts (review uploaded receipts) =====
    adminListReceipts: (params) => client.get('/admin/receipts', { params }),
    adminApproveReceipt: (receiptId, payload) => client.post(`/admin/receipts/${receiptId}/approve`, payload || {}),
    adminRejectReceipt: (receiptId, payload) => client.post(`/admin/receipts/${receiptId}/reject`, payload || {}),
    fetchReceiptFile: (receiptId) => client.get(`/receipts/files/${receiptId}`, { responseType: 'blob' }),

    // ===== Device-bound PIN unlock =====
    pinStatus: (deviceId) => client.get('/auth/pin/status', { params: { device_id: deviceId } }),
    pinDeviceStatus: (deviceId) => client.get('/auth/pin/device-status', { params: { device_id: deviceId } }),
    pinSet: (deviceId, pin) => client.post('/auth/pin/set', { device_id: deviceId, pin }),
    pinVerify: (payload) => client.post('/auth/pin/verify', payload),
    pinClear: (deviceId) => client.post('/auth/pin/clear', { device_id: deviceId }),

    // ===== REMOVED in v1 (deferred to v2) =====
    // Nia Plus subscription · Support Buffer (bridges) · Emergency requests
    // · Approval rules · Local Agent Network · WhatsApp inbound requests
    // · Sender↔Agent chat · Community fundraisers · Release tickets / SLA
    // The backend endpoints for these are also removed in v1; calls would 404.
};

/**
 * Trigger a CSV download from the admin exports endpoint while keeping the
 * Bearer token in the header (never on the URL).
 */
export async function adminDownloadCsv(kind) {
    const res = await client.get(`/admin/exports/${kind}.csv`, { responseType: 'blob' });
    const blob = new Blob([res.data], { type: 'text/csv' });
    const url = window.URL.createObjectURL(blob);
    const a = document.createElement('a');
    a.href = url;
    a.download = `nia-${kind}.csv`;
    document.body.appendChild(a);
    a.click();
    a.remove();
    window.URL.revokeObjectURL(url);
}


// ---------------------------------------------------------------------------
// Error helpers
// ---------------------------------------------------------------------------

/**
 * Pull a human-readable error message out of an axios error.
 *
 * FastAPI's HTTPException `detail` can be either a string OR an object
 * (e.g. rate-limit responses use `{error, retry_after_seconds, message}`).
 * Passing the object directly to `toast.error()` causes Sonner to render
 * it as a React child and crash with:
 *   "Objects are not valid as a React child"
 *
 * This helper normalises any of those shapes into a plain string so the
 * UI never crashes on a backend error.
 */
export function extractErrorMessage(err, fallback = 'Something went wrong') {
    const detail = err?.response?.data?.detail;
    if (typeof detail === 'string' && detail.trim()) return detail;
    if (detail && typeof detail === 'object') {
        // Try friendly fields first, in priority order.
        if (typeof detail.message === 'string' && detail.message.trim()) return detail.message;
        if (typeof detail.error === 'string' && detail.error.trim()) {
            // Add a friendly suffix for rate-limit shapes when we have it.
            if (typeof detail.retry_after_seconds === 'number' && detail.retry_after_seconds > 0) {
                return `${humaniseErrorCode(detail.error)} Try again in ${detail.retry_after_seconds}s.`;
            }
            return humaniseErrorCode(detail.error);
        }
    }
    // Fallback path: top-level message string on the axios error itself.
    if (typeof err?.message === 'string' && err.message && err.message !== 'Network Error') {
        return err.message;
    }
    return fallback;
}

function humaniseErrorCode(code) {
    return String(code)
        .replace(/_/g, ' ')
        .replace(/\b\w/g, (c) => c.toUpperCase());
}
